import { useRef } from "react";
import { Icon } from "@/components/ui/Icon";
import { IconButton } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { AvatarStack } from "@/components/ui/Avatar";
import { SCALE, statusColorOf, tagColor } from "@/lib/tokens";
import { DoneBox } from "@/components/ui/DoneBox";
import { fmtCurrency, relDate } from "@/lib/format";
import { useDateFormat } from "@/hooks/useDateFormat";
import { isTicketBoard, ticketLabel } from "@/lib/boardView";
import { TicketAgeChip, TicketIdChip } from "@/components/board/TicketChips";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { ViewBoard, ViewRow } from "@/lib/boardView";
import type { AvatarUser } from "@/components/ui/Avatar";
import type { UUID } from "@/lib/types";

interface MobileRowCardProps {
  row: ViewRow;
  board: ViewBoard;
  /** Every face this board may draw — members ∪ guests (`lib/boardPeople`). 🔴 Passed in, not
   *  looked up: this used to resolve owners through `userDirectory.byId`, which searches the
   *  DEMO FIXTURE ("u1".."u6"), so a real UUID owner NEVER matched and the avatar stack was
   *  silently empty on every card on every phone. */
  people: Map<UUID, AvatarUser>;
  onTap: () => void;
  onCheck: (v: boolean) => void;
}

export function MobileRowCard({ row, board, people, onTap, onCheck }: MobileRowCardProps) {
  const [dateFormat] = useDateFormat();
  const statusCol = board.columns.find((c) => c.type === "status");
  const statusOpt = statusCol?.options?.find((o) => o.id === row.status);
  const tint = statusColorOf(statusOpt?.color);
  const owners = (row.owners ?? [])
    .map((id) => people.get(id as UUID))
    .filter((u): u is AvatarUser => Boolean(u));
  // A completed row (checked task / resolved ticket) is never "überfällig".
  const rel = relDate(
    row.due,
    undefined,
    row.done || !!row.raw?.resolved_at,
    undefined,
    dateFormat,
  );
  const ticketing = isTicketBoard(board);
  const idLabel = ticketing ? ticketLabel(board.ticketPrefix, row.ticketNumber) : null;
  const ref = useRef<HTMLDivElement | null>(null);
  return (
    <div
      ref={ref}
      onClick={onTap}
      onTouchStart={() => {
        if (ref.current) ref.current.style.transform = "scale(0.98)";
      }}
      onTouchEnd={() => {
        if (ref.current) ref.current.style.transform = "scale(1)";
      }}
      style={{
        background: "var(--surface)",
        borderRadius: 12,
        padding: "14px 14px",
        marginBottom: 8,
        border: "1px solid var(--hairline)",
        position: "relative",
        overflow: "hidden",
        transition: "transform .12s",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {statusOpt ? (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: tint.dot,
          }}
        />
      ) : null}

      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCheck(!row.done);
          }}
          aria-label="Toggle done"
          style={{
            width: 26,
            height: 26,
            marginTop: -2,
            padding: 0,
            borderRadius: 8,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <DoneBox checked={row.done} size={20} />
        </button>
        <h3
          className="flow-strike"
          data-done={row.done}
          style={{
            flex: 1,
            margin: 0,
            fontSize: 15,
            fontWeight: 500,
            color: "var(--ink-1)",
            letterSpacing: "-0.005em",
            lineHeight: 1.3,
            opacity: row.done ? 0.55 : 1,
            transition: "opacity var(--flow-motion-fast) var(--flow-ease-out)",
          }}
        >
          {row.title}
        </h3>
        <IconButton
          icon="dotsV"
          size={28}
          title={t.comingSoon}
          onClick={(e) => {
            e.stopPropagation();
            showToast(t.comingSoon);
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
          paddingLeft: 38,
        }}
      >
        {idLabel ? <TicketIdChip label={idLabel} /> : null}
        {statusOpt ? <StatusPill option={statusOpt} size="sm" /> : null}
        {ticketing ? (
          <TicketAgeChip createdAt={row.raw?.created_at} resolvedAt={row.raw?.resolved_at} />
        ) : null}
        {rel ? (
          <span
            style={{
              fontSize: 11.5,
              padding: "3px 8px",
              borderRadius: 999,
              background: rel.overdue
                ? "rgba(184, 82, 73, 0.10)"
                : rel.today
                  ? "rgba(183, 128, 41, 0.12)"
                  : "var(--surface-soft)",
              color: rel.overdue ? "var(--danger)" : rel.today ? "var(--warn)" : "var(--ink-2)",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontFeatureSettings: '"tnum"',
            }}
          >
            <Icon name="date" size={11} /> {rel.label}
          </span>
        ) : null}
        {owners.length > 0 ? <AvatarStack users={owners} size={18} max={3} /> : null}
        {row.budget && row.budget > 0 ? (
          <span
            style={{
              fontSize: 11,
              color: "var(--ink-2)",
              fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
              marginLeft: "auto",
            }}
          >
            {fmtCurrency(row.budget)}
          </span>
        ) : null}
      </div>
      {row.tags && row.tags.length > 0 ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            marginTop: 8,
            paddingLeft: 38,
          }}
        >
          {row.tags.slice(0, 4).map((tag) => (
            <span
              key={tag}
              style={{
                fontSize: 10.5,
                padding: "2px 7px",
                borderRadius: 4,
                fontWeight: 500,
                background: tagColor(tag),
                color: SCALE[700],
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
